// frontend/modules/customer-category/hooks/useProducts.ts
import { useCallback, useRef } from 'react';
import { useCustomerCategoryStore } from '../stores/customer-category-store';
import { message } from 'antd';
import { 
    useGetAllProducts1, 
    useGetProductsByType1, 
    useGetProductsForCategory, 
    useCheckAccountBalance 
} from '@/shared/api/client/customer-product-controller';
import { ProductDTO, CustomerCategoryProductDTO } from '@/shared/api/model';

export const useProducts = () => {
    const {
        allProducts,
        categoryProducts,
        isLoading,
        error,

        setAllProducts,
        setCategoryProducts,
        setIsLoading,
        setError
    } = useCustomerCategoryStore();

    // Use orval-generated hook for fetching all products
    const { 
        data: allProductsData, 
        isLoading: isLoadingAllProducts, 
        error: allProductsError,
        refetch: refetchAllProducts 
    } = useGetAllProducts1();

    // Store refetch function in a ref to avoid dependency issues
    const refetchAllProductsRef = useRef(refetchAllProducts);
    refetchAllProductsRef.current = refetchAllProducts;

    // Fetch all available products
    const fetchAllProducts = useCallback(async () => {
        try {
            setIsLoading(true);
            const result = await refetchAllProductsRef.current();
            if (result.data) {
                setAllProducts(result.data);
            }
            setError(null); // Clear any previous errors
        } catch (error: any) {
            console.error('Error fetching products:', error);
            setError(error.message || 'Failed to fetch products');
            message.error('Failed to fetch products');
        } finally {
            setIsLoading(false);
        }
    }, []); // Empty dependency array since we're using the latest state via setters

    // Fetch products by type using direct API call (since orval hooks are not easily dynamic)
    const fetchProductsByType = useCallback(async (productType: string): Promise<ProductDTO[]> => {
        try {
            setIsLoading(true);
            const response = await fetch(`/api/products/type/${productType}`);
            if (response.ok) {
                const data = await response.json();
                return data as ProductDTO[];
            } else {
                throw new Error('Failed to fetch products by type');
            }
        } catch (error: any) {
            console.error('Error fetching products by type:', error);
            setError(error.message || 'Failed to fetch products');
            message.error('Failed to fetch products');
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [setIsLoading, setError]);

    // Fetch products for a specific category
    const fetchCategoryProducts = useCallback(async (categoryId: number) => {
        try {
            setIsLoading(true);
            const response = await fetch(`/api/products/category/${categoryId}`);
            if (response.ok) {
                const data = await response.json();
                setCategoryProducts(data as CustomerCategoryProductDTO[]);
            } else {
                throw new Error('Failed to fetch category products');
            }
        } catch (error: any) {
            console.error('Error fetching category products:', error);
            setError(error.message || 'Failed to fetch category products');
            message.error('Failed to fetch category products');
        } finally {
            setIsLoading(false);
        }
    }, [setCategoryProducts, setIsLoading, setError]);

    // Check if an account has balance
    const checkAccountBalance = useCallback(async (branch: string, glNo: string, acNo: string): Promise<boolean> => {
        try {
            setIsLoading(true);
            const response = await fetch(`/api/products/check-balance?branch=${encodeURIComponent(branch)}&glNo=${encodeURIComponent(glNo)}&acNo=${encodeURIComponent(acNo)}`);
            if (response.ok) {
                const data = await response.json();
                return data.hasBalance as boolean;
            } else {
                throw new Error('Failed to check account balance');
            }
        } catch (error: any) {
            console.error('Error checking account balance:', error);
            setError(error.message || 'Failed to check account balance');
            message.error('Failed to check account balance');
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [setIsLoading, setError]);

    return {
        allProducts,
        categoryProducts,
        isLoading,
        error,

        fetchAllProducts,
        fetchProductsByType,
        fetchCategoryProducts,
        checkAccountBalance
    };
};